import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import config from '../config';
import HistoryList from '../components/HistoryList';
import ExemplosSection from '../components/ExemplosSection';
import { 
  UserIcon, 
  BriefcaseIcon, 
  ChatBubbleLeftRightIcon, 
  LightBulbIcon, 
  SparklesIcon
} from '@heroicons/react/24/solid';

export default function InterviewSimulator() {
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [experience, setExperience] = useState('junior');
  const [simulation, setSimulation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [visibleTips, setVisibleTips] = useState({});

  // ESTADO PARA O POP-UP DE CRÉDITOS
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  useEffect(() => {
    const handleLoadFromHistory = (event) => {
      if (event.detail && event.detail.text) {
        setRole(event.detail.text); 
        setShowHistory(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    window.addEventListener('loadFromHistory', handleLoadFromHistory);
    return () => window.removeEventListener('loadFromHistory', handleLoadFromHistory);
  }, []);

  const toggleTip = (index) => {
    setVisibleTips(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleStartSimulation = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSimulation(null);
    setVisibleTips({});

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Faça login para iniciar a simulação.');

      const response = await fetch(config.ENDPOINTS.MOCK_INTERVIEW, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role,
          company,
          description,
          experience,
          user_id: user.id
        }),
      });

      // LÓGICA DE BLOQUEIO DE CRÉDITOS (Erro 402)
      if (response.status === 402) {
        setShowUpgradeModal(true);
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao iniciar simulação.');

      setSimulation(data);

      try {
        await fetch(`${config.API_BASE_URL}/save-history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            tool_type: 'interview',
            input_data: `${role} na ${company}`,
            output_data: JSON.stringify(data),
            metadata: { experience, description_snippet: description.substring(0, 50) }
          })
        });
      } catch (histError) {
        console.error("Erro ao salvar histórico:", histError);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', 
    height: '50px', 
    padding: '0 15px', 
    borderRadius: '10px', 
    backgroundColor: '#111827', 
    color: 'white', 
    border: '1px solid #4b5563',
    boxSizing: 'border-box',
    fontSize: '1rem',
    outline: 'none',
    margin: 0
  };

  const labelStyle = {
    display: 'block', 
    marginBottom: '8px', 
    fontSize: '0.9rem', 
    color: '#9ca3af', 
    lineHeight: '1.5',
    fontWeight: '600'
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#0f1016', // Fundo Dark Sólido e Puro
      backgroundImage: 'none', // FORÇA A REMOÇÃO DO DEGRADÊ ANTIGO
      color: 'white', 
      padding: '40px 20px', 
      fontFamily: "'Inter', sans-serif",
      position: 'relative',
      overflow: 'hidden'
    }}>
      
      {/* LUZ VERDE CONCENTRADA APENAS NO TOPO */}
      <div style={{
        position: 'absolute',
        top: '-150px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '500px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, rgba(15, 16, 22, 0) 70%)',
        filter: 'blur(50px)',
        zIndex: 0,
        pointerEvents: 'none'
      }}></div>

      {/* CSS RESPONSIVO */}
      <style>{`
        .tool-grid {
          display: grid;
          gap: 30px;
          grid-template-columns: 1fr;
        }
        
        @media (min-width: 1024px) {
          .tool-grid {
            grid-template-columns: 1fr 1fr;
            grid-auto-rows: 1fr;
          }
        }

        .tool-card {
          height: 100%;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          position: relative;
          z-index: 1;
        }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .loader { border: 3px solid #374151; border-top: 3px solid #10b981; border-radius: 50%; width: 30px; height: 30px; animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        
        {/* CABEÇALHO */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <div style={{ 
             display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
             background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
             width: '60px', height: '60px', borderRadius: '15px', marginBottom: '20px',
             boxShadow: '0 10px 30px -10px rgba(16, 185, 129, 0.5)'
          }}>
            <ChatBubbleLeftRightIcon style={{ width: '32px', color: 'white' }} />
          </div>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '800', 
            marginBottom: '10px',
            background: 'linear-gradient(to right, #ffffff, #6ee7b7, #10b981)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Simulador de Entrevista
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
            Pratique com um Recrutador IA que cria perguntas baseadas na sua vaga real.
          </p>
        </div>

        {/* BOTÃO HISTÓRICO */}
        {user && (
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{
                padding: '10px 20px',
                backgroundColor: showHistory ? '#374151' : 'rgba(255,255,255,0.05)',
                color: '#d1d5db',
                border: '1px solid #374151',
                borderRadius: '50px',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                fontSize: '0.9rem', fontWeight: '500'
              }}
            >
              <BriefcaseIcon style={{ width: '16px' }} />
              {showHistory ? 'Ocultar Histórico' : 'Ver Simulações Anteriores'}
            </button>
          </div>
        )}

        {showHistory && user && (
          <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#1f2937', borderRadius: '16px', border: '1px solid #374151' }}>
            <HistoryList user={user} toolType="interview" />
          </div>
        )}

        {/* GRID PRINCIPAL */}
        <div className="tool-grid">
          
          {/* LADO ESQUERDO: CONFIGURAÇÃO */}
          <div className="tool-card" style={{ 
            backgroundColor: '#1f2937', 
            padding: '30px', 
            borderRadius: '20px', 
            border: '1px solid #374151',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <form onSubmit={handleStartSimulation} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' }}>
              
              {/* LINHA 1: CARGO */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                  Cargo Alvo:
                </label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Ex: Desenvolvedor Front-end"
                  required
                  style={inputStyle}
                />
              </div>

              {/* LINHA 2: EMPRESA E NÍVEL */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                    <label style={labelStyle}>
                      Empresa:
                    </label>
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Ex: Google"
                      style={inputStyle}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={labelStyle}>
                      Nível:
                    </label>
                    <select
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      style={{ 
                        ...inputStyle,
                        cursor: 'pointer',
                        appearance: 'none', 
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 10px center',
                        backgroundSize: '16px'
                      }}
                    >
                        <option value="intern">Estagiário</option>
                        <option value="junior">Júnior</option>
                        <option value="mid">Pleno</option>
                        <option value="senior">Sênior</option>
                        <option value="lead">Liderança</option>
                    </select>
                </div>
              </div>

              <div style={{ marginBottom: '25px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <label style={labelStyle}>
                  Descrição da Vaga (Cole aqui):
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Cole os requisitos da vaga para a IA gerar perguntas ultra-específicas..."
                  style={{ 
                    width: '100%', 
                    flexGrow: 1, 
                    minHeight: '200px', 
                    padding: '15px', 
                    borderRadius: '10px', 
                    backgroundColor: '#111827', 
                    color: 'white', 
                    border: '1px solid #4b5563', 
                    boxSizing: 'border-box', 
                    resize: 'none',
                    lineHeight: '1.5',
                    fontSize: '1rem',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  marginTop: 'auto',
                  width: '100%',
                  padding: '16px',
                  background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  cursor: isLoading ? 'wait' : 'pointer',
                  fontSize: '1.1rem',
                  opacity: isLoading ? 0.7 : 1,
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                  transition: 'transform 0.1s'
                }}
              >
                {isLoading ? '🤖 Gerando Entrevista...' : '🚀 Iniciar Simulação'}
              </button>
            </form>
          </div>

          {/* LADO DIREITO: CHAT INTERATIVO */}
          <div className="tool-card" style={{ 
            backgroundColor: '#1f2937', 
            borderRadius: '20px', 
            border: '1px solid #10b981', 
            display: 'flex',
            flexDirection: 'column',
            minHeight: '600px', 
            overflow: 'hidden'
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #374151', backgroundColor: '#111827', display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserIcon style={{ width: '26px', color: 'white' }} />
                </div>
                <div>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#fff' }}>Recrutador IA</h3>
                    <span style={{ fontSize: '0.85rem', color: '#10b981' }}>● Online agora</span>
                </div>
            </div>

            <div style={{ flexGrow: 1, padding: '25px', overflowY: 'auto', backgroundColor: '#111827' }}>
                {!simulation && !isLoading && (
                    <div style={{ textAlign: 'center', color: '#6b7280', marginTop: '100px', padding: '0 20px' }}>
                        <ChatBubbleLeftRightIcon style={{ width: '80px', margin: '0 auto 20px', opacity: 0.1 }} />
                        <p style={{fontSize: '1.1rem'}}>Preencha os dados da vaga ao lado e clique em iniciar.</p>
                        <p style={{fontSize: '0.9rem'}}>Eu vou criar um roteiro personalizado para você treinar.</p>
                    </div>
                )}

                {isLoading && (
                    <div style={{ textAlign: 'center', marginTop: '120px' }}>
                        <div className="loader" style={{ margin: '0 auto 20px' }}></div>
                        <p style={{ color: '#10b981' }}>Analisando perfil da vaga...</p>
                    </div>
                )}

                {simulation && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        <div style={{ alignSelf: 'flex-start', maxWidth: '90%' }}>
                            <div style={{ backgroundColor: '#374151', padding: '15px 20px', borderRadius: '20px 20px 20px 5px', color: '#e5e7eb', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                                Olá! Sou seu recrutador virtual. Com base na vaga de <strong>{role}</strong>, preparei algumas perguntas que você provavelmente vai enfrentar. Vamos lá?
                            </div>
                        </div>

                        {simulation.questions && simulation.questions.map((q, idx) => (
                            <div key={idx} style={{ alignSelf: 'flex-start', maxWidth: '95%', animation: `fadeIn 0.5s ease forwards ${idx * 0.2}s`, opacity: 0 }}>
                                <div style={{ marginBottom: '5px', fontSize: '0.8rem', color: '#9ca3af', marginLeft: '10px' }}>Pergunta {idx + 1}</div>
                                <div style={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', padding: '15px 20px', borderRadius: '20px 20px 20px 5px', color: '#fff', marginBottom: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                                    {q.q}
                                </div>
                                
                                <button 
                                    onClick={() => toggleTip(idx)}
                                    style={{ 
                                        background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', 
                                        fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '10px'
                                    }}
                                >
                                    <LightBulbIcon style={{ width: '16px' }} /> 
                                    {visibleTips[idx] ? 'Ocultar Dica' : 'Ver Resposta Ideal'}
                                </button>

                                {visibleTips[idx] && (
                                    <div style={{ marginTop: '10px', backgroundColor: 'rgba(16, 185, 129, 0.1)', padding: '15px', borderRadius: '10px', borderLeft: '3px solid #10b981', color: '#d1d5db', fontSize: '0.9rem', marginLeft: '10px' }}>
                                            <strong>💡 Dica do Especialista:</strong> {q.a}
                                    </div>
                                )}
                            </div>
                        ))}

                        <div style={{ marginTop: '30px', borderTop: '1px solid #374151', paddingTop: '20px' }}>
                            <h4 style={{ color: '#fbbf24', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <SparklesIcon style={{width: '20px'}}/> Feedback Geral & Pontos de Atenção:
                            </h4>
                            <ul style={{ paddingLeft: '20px', color: '#9ca3af', lineHeight: '1.6' }}>
                                {simulation.tips && simulation.tips.map((tip, idx) => (
                                    <li key={idx} style={{ marginBottom: '8px' }}>{tip}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
          </div>

        </div>

        <ExemplosSection ferramentaId="interview-simulator" />
      </div>

      {showUpgradeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            backgroundColor: '#1f2937', padding: '40px', borderRadius: '24px',
            maxWidth: '420px', width: '90%', textAlign: 'center',
            border: '1px solid #10b981', boxShadow: '0 10px 50px rgba(16, 185, 129, 0.3)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '10px' }}>🪙</div>
            <h2 style={{ color: 'white', marginBottom: '15px', fontSize: '1.8rem', fontWeight: '800' }}>
              Créditos Esgotados!
            </h2>
            <p style={{ color: '#9ca3af', marginBottom: '30px', fontSize: '1.05rem', lineHeight: '1.5' }}>
              Você atingiu o limite do plano gratuito. Assine o <strong>Plano PRO</strong> para criar sem limites e ter acesso a todas as ferramentas premium.
            </p>
            <Link to="/precos" style={{
              display: 'block', width: '100%', padding: '16px', boxSizing: 'border-box',
              background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', color: 'white',
              borderRadius: '12px', fontWeight: 'bold', textDecoration: 'none',
              marginBottom: '15px', fontSize: '1.1rem', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
            }}>
              Ver Planos PRO 🚀
            </Link>
            <button 
              onClick={() => setShowUpgradeModal(false)} 
              style={{
                background: 'transparent', border: 'none', color: '#9ca3af',
                cursor: 'pointer', fontSize: '0.95rem', textDecoration: 'underline'
              }}
            >
              Voltar para a ferramenta
            </button>
          </div>
        </div>
      )}

    </div>
  );
}